"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { Device, Area, EnergyData, UserPreferences, Activity, DeviceState } from "@/types";
import { useMockDatabase } from "@/hooks/useMockDatabase";

interface MockDataContextType {
  devices: Device[];
  areas: Area[];
  energyData: EnergyData[];
  userPreferences: UserPreferences;
  activities: Activity[];
  updateDevice: (deviceId: string, updates: Partial<DeviceState>) => void;
  updateArea: (areaId: string, updates: Partial<Area>) => void;
  updateEnergyData: (deviceId: string, data: Partial<EnergyData>) => void;
  updateUserPreferences: (updates: Partial<UserPreferences>) => void;
  addActivity: (activity: Omit<Activity, "id">) => void;
}

const MockDataContext = createContext<MockDataContextType | undefined>(
  undefined,
);

export function MockDataProvider({ children }: { children: React.ReactNode }) {
  const mockDb = useMockDatabase();
  const [devices, setDevices] = useState<Device[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [energyData, setEnergyData] = useState<EnergyData[]>([]);
  const [userPreferences, setUserPreferences] = useState<UserPreferences | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);

  useEffect(() => {
    if (mockDb) {
      // Initialize state from database
      setDevices(mockDb.getDevices());
      setAreas(mockDb.getAreas());
      setEnergyData(mockDb.getEnergyData());
      setUserPreferences(mockDb.getUserPreferences());
      setActivities(mockDb.getActivities());

      // Subscribe to device state changes
      const handleDeviceUpdate = (deviceId: string, state: DeviceState) => {
        setDevices(mockDb.getDevices());
      };

      mockDb.subscribe("mock-data-context", handleDeviceUpdate);

      return () => {
        mockDb.unsubscribe("mock-data-context");
      };
    }
  }, [mockDb]);

  // Don't render anything until we have the database and initial data
  if (!mockDb || !userPreferences) {
    return null;
  }

  const updateDevice = (deviceId: string, updates: Partial<DeviceState>) => {
    mockDb.updateDevice(deviceId, updates);
    setDevices(mockDb.getDevices());
  };

  const updateArea = (areaId: string, updates: Partial<Area>) => {
    mockDb.updateArea(areaId, updates);
    setAreas(mockDb.getAreas());
  };

  const updateEnergyData = (deviceId: string, data: Partial<EnergyData>) => {
    mockDb.addEnergyData({
      ...data,
      deviceId,
      timestamp: new Date().toISOString(),
      powerConsumption: data.powerConsumption ?? 0
    });
    setEnergyData(mockDb.getEnergyData());
  };

  const updateUserPreferences = (updates: Partial<UserPreferences>) => {
    mockDb.updateUserPreferences(updates);
    setUserPreferences(mockDb.getUserPreferences());
  };

  const addActivity = (activity: Omit<Activity, "id">) => {
    mockDb.addActivity(activity);
    setActivities(mockDb.getActivities());
  };

  return (
    <MockDataContext.Provider
      value={{
        devices,
        areas,
        energyData,
        userPreferences,
        activities,
        updateDevice,
        updateArea,
        updateEnergyData,
        updateUserPreferences,
        addActivity,
      }}
    >
      {children}
    </MockDataContext.Provider>
  );
}

export function useMockData() {
  const context = useContext(MockDataContext);
  if (context === undefined) {
    throw new Error("useMockData must be used within a MockDataProvider");
  }
  return context;
}
